import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserData } from '../app/auth/authSlice';
import userDataService from '../services/userDataService';

const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

/**
 * Custom hook to handle automatic user data refresh
 * Refreshes profile and settings data every 10 minutes
 */
export const useUserDataRefresh = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const lastDataRefresh = useSelector(state => state.auth.lastDataRefresh);
  const profileData = useSelector(state => state.auth.profileData);
  const settingsData = useSelector(state => state.auth.settingsData);
  const intervalRef = useRef(null);
  const lastDataRefreshRef = useRef(lastDataRefresh);
  const refreshingRef = useRef(false);
  const profileDataRef = useRef(profileData);
  const settingsDataRef = useRef(settingsData);

  useEffect(() => {
    lastDataRefreshRef.current = lastDataRefresh;
  }, [lastDataRefresh]);

  useEffect(() => {
    profileDataRef.current = profileData;
    settingsDataRef.current = settingsData;
  }, [profileData, settingsData]);

  const refreshUserData = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const userData = await userDataService.fetchUserData();
      const currentProfile = profileDataRef.current;
      const currentSettings = settingsDataRef.current;
      const profileUnchanged =
        userData.profileData &&
        currentProfile &&
        JSON.stringify(userData.profileData) === JSON.stringify(currentProfile);
      const settingsUnchanged =
        userData.settingsData &&
        currentSettings &&
        JSON.stringify(userData.settingsData) === JSON.stringify(currentSettings);

      if (profileUnchanged && settingsUnchanged) {
        dispatch(updateUserData({ lastDataRefresh: userData.lastDataRefresh }));
        return;
      }

      dispatch(updateUserData(userData));
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    } finally {
      refreshingRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const lastRefresh = lastDataRefreshRef.current
      ? new Date(lastDataRefreshRef.current)
      : null;
    const shouldRefreshImmediately =
      !lastRefresh || (Date.now() - lastRefresh.getTime()) >= REFRESH_INTERVAL;

    if (shouldRefreshImmediately) {
      refreshUserData();
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      refreshUserData();
    }, REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Intentionally depend only on auth + stable refresh fn — not lastDataRefresh,
    // which would restart this effect after every successful refresh.
  }, [isAuthenticated, refreshUserData]);

  return {
    refreshUserData
  };
};

export default useUserDataRefresh;
